import { env } from '@shared/config';
import { maskSensitive, maskText } from '@shared/lib/logger';

/**
 * 클라이언트 에러 수집. 지연 초기화 싱글턴이다.
 * - DSN 이 없으면 no-op
 * - 어떤 호출도 throw 하지 않는다
 * - 필드 · 마스킹은 X5(observability.md)를 따른다. X5 가 아직 없어 아래 형태는 제안이다.
 */
export interface ErrorReport {
  message: string;
  name: string;
  stack?: string;
  traceId?: string;
  /** 경로만. 쿼리 문자열은 보내지 않는다(토큰이 든 링크가 있을 수 있다) */
  route: string;
  env: string;
  /** ISO 8601 UTC */
  timestamp: string;
  context?: Record<string, unknown>;
}

export type ErrorReportTransport = (report: ErrorReport) => void;

export interface ErrorReporterOptions {
  dsn: string | undefined;
  env: string;
  transport?: ErrorReportTransport;
}

function beaconTransport(dsn: string): ErrorReportTransport {
  return (report) => {
    navigator.sendBeacon(dsn, new Blob([JSON.stringify(report)], { type: 'application/json' }));
  };
}

export function createErrorReporter(options: ErrorReporterOptions) {
  let transport: ErrorReportTransport | null | undefined;

  function resolveTransport(): ErrorReportTransport | null {
    if (transport !== undefined) return transport;
    if (!options.dsn) {
      transport = null;
    } else {
      transport = options.transport ?? beaconTransport(options.dsn);
    }
    return transport;
  }

  return {
    get enabled() {
      return Boolean(options.dsn);
    },
    capture(error: unknown, context?: Record<string, unknown> & { traceId?: string }) {
      try {
        const send = resolveTransport();
        if (!send) return;
        const { traceId, ...rest } = context ?? {};
        const source = error instanceof Error ? error : new Error(String(error));
        send({
          name: source.name,
          message: maskText(source.message),
          ...(source.stack ? { stack: maskText(source.stack) } : {}),
          ...(traceId ? { traceId } : {}),
          route: globalThis.location?.pathname ?? '',
          env: options.env,
          timestamp: new Date().toISOString(),
          ...(Object.keys(rest).length > 0
            ? { context: maskSensitive(rest) as Record<string, unknown> }
            : {}),
        });
      } catch {
        // 수집 실패가 화면을 깨지 않게 한다.
      }
    },
  };
}

export type ErrorReporter = ReturnType<typeof createErrorReporter>;

export const errorReporter: ErrorReporter = createErrorReporter({
  dsn: env.errorReportDsn,
  env: env.appEnv,
});
